import React, { useRef, useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CircularProgress, Alert, Button } from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ContentCopyOutlined as ContentCopyOutlinedIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
  PictureAsPdfOutlined as PictureAsPdfOutlinedIcon,
  AddCircleOutline as AddCircleOutlineIcon,
} from "@mui/icons-material";
import Markdown from "react-markdown";
import foundryMarkdown from "../../../lib/FoundryMarkdown";
import { markdownComponents } from "../../components/MarkdownComponents/MarkdownComponents";
import InternalLinkSourcePill from "../../components/InternalLinkSourcePill/InternalLinkSourcePill";
import {
  BlockDocumentRenderer,
  TocBlockRenderer,
} from "../../components/BlockDocumentRenderer/BlockDocumentRenderer";
import { BlockDocumentMap } from "../../components/BlockDocumentMap/BlockDocumentMap";
import { KeyEventsChart } from "../../components/KeyEventsChart/KeyEventsChart";
import type { TocBlock, TocEntry } from "@unruly-global/api-types";
import {
  ShareDialog,
  ShareDialogHandle,
} from "../../components/ShareDialog/ShareDialog";
import {
  SharedToLinkedInToast,
  SharedToLinkedInToastHandle,
} from "../../components/SharedToLinkedInToast/SharedToLinkedInToast";
import CreateDyadDialog from "../../components/Dialog/CreateDyadDialog";
import type { DialogHandle } from "../../components/Dialog/DialogTypes";
import { useServices } from "../../services/Services";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { usePageTitle } from "../../context/usePageTitle";
import { useEventSources } from "../../hooks/useEventSources";
import {
  cleanHtmlForCopy,
  extractFoundryObjectReferences,
  buildSourceNumberMap,
} from "../../helper/helper";
import {
  parseBlockContent,
  collectBlockDocumentSources,
} from "../../helper/blockDocument";
import {
  setCentroidData,
  buildLocationClusters,
  type LocationInput,
} from "../../helper/resolveLocation";
import { exportCollectionPdf } from "../../helper/exportCollectionPdf";
import { format } from "date-fns";
import type { Osdk } from "@osdk/client";
import type { Analysis } from "@unruly-osdk/sdk";
import type { EntityItem } from "../../common-types/Entity";
import styles from "./CustomReportPage.module.css";

export const CustomReportPage: React.FC = () => {
  const services = useServices();
  const navigate = useNavigate();
  const params = useParams<{ reportId: string }>();
  const reportId = params.reportId;
  const contentRef = useRef<HTMLDivElement>(null);
  const keyEventsRef = useRef<HTMLDivElement>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const { setPageTitle } = usePageTitle();
  const shareDialogRef = useRef<ShareDialogHandle>(null);
  const toastRef = useRef<SharedToLinkedInToastHandle>(null);
  const createDyadDialogRef = useRef<DialogHandle>(null);

  const { hasAccess, isLoading: isAccessLoading } =
    services.User.useIsInCustomReportsGroup();

  const {
    data: report,
    isLoading,
    isError,
  } = services.CustomReport.useGetCustomReportById(reportId || "");

  const { data: user } = services.User.useGetCurrent();

  // Fetch OSDK geo-coord library and replace static centroids when available
  const { data: geoCoords } = services.Report.useGetGeoCoordLibrary();
  useEffect(() => {
    if (geoCoords) setCentroidData(geoCoords);
  }, [geoCoords]);

  // Detect whether the report uses the new Block Document format or legacy markdown
  // Version 2.0+ stores content as block JSON; type is the report category (e.g. "regional", "geopolitical")
  const isBlockDocument = report?.version === "2.0";

  const blockDocument = useMemo(
    () => (isBlockDocument ? parseBlockContent(report?.content ?? "") : null),
    [isBlockDocument, report?.content],
  );

  // Build location clusters for PDF export (same logic as BlockDocumentMap)
  const clusters = useMemo(() => {
    if (!blockDocument || report?.type !== "collection") return [];
    const inputs: LocationInput[] = [];
    for (const block of blockDocument.blocks) {
      if ("location" in block && block.location) {
        inputs.push({
          uid: block.uid,
          location: block.location,
          title: "title" in block ? (block.title as string) : undefined,
          blurb:
            "location_blurb" in block
              ? (block.location_blurb as string)
              : undefined,
        });
      }
    }
    return buildLocationClusters(inputs);
  }, [blockDocument, report?.type]);

  // Extract TOC block (if any) and the remaining blocks for the renderer.
  // For collection reports, generate the TOC from section blocks so all
  // sub-reports are represented — the pipeline TOC may only cover one.
  const tocBlock = useMemo<TocBlock | null>(() => {
    if (!blockDocument) return null;

    if (report?.type === "collection") {
      const blocks = blockDocument.blocks;
      const entries: TocEntry[] = [];
      let currentEntry: TocEntry | null = null;

      for (const b of blocks) {
        if (b.type === "section" && b.title) {
          currentEntry = {
            heading: b.title,
            subEntries: [],
            targetUid: b.uid,
          };
          entries.push(currentEntry);
        } else if (
          currentEntry &&
          "title" in b &&
          b.title &&
          b.type !== "toc"
        ) {
          currentEntry.subEntries.push(b.title);
        }
      }

      entries.push({
        heading: "Global Key Events",
        subEntries: [],
        targetUid: "key-events-chart",
      });

      if (entries.length === 0) return null;
      return {
        uid: "toc-generated",
        type: "toc" as const,
        entries,
      };
    }

    const toc = blockDocument.blocks.find(
      (b): b is TocBlock => b.type === "toc",
    );
    return toc ?? null;
  }, [blockDocument, report?.type]);

  const blockDocumentWithoutToc = useMemo(() => {
    if (!blockDocument || !tocBlock) return blockDocument;
    return {
      ...blockDocument,
      blocks: blockDocument.blocks.filter((b) => b.type !== "toc"),
    };
  }, [blockDocument, tocBlock]);

  // For markdown reports: extract inline object references
  const markdownReferencedEvents = useMemo(
    () =>
      isBlockDocument
        ? []
        : extractFoundryObjectReferences(report?.content ?? ""),
    [isBlockDocument, report?.content],
  );

  // For block documents: build EntityItem[] from source arrays
  const blockDocumentReferencedEvents: EntityItem[] = useMemo(() => {
    if (!blockDocument) return [];
    return collectBlockDocumentSources(blockDocument).map((id) => ({
      id,
      name: "",
      type: "event" as const,
    }));
  }, [blockDocument]);

  const referencedEvents = isBlockDocument
    ? blockDocumentReferencedEvents
    : markdownReferencedEvents;

  const sourceNumberMap = buildSourceNumberMap(referencedEvents);
  const {
    eventSources,
    isLoading: isEventSourcesLoading,
    missingEventIds,
  } = useEventSources(referencedEvents);

  const handleOpenAnalysis = (
    analysis: Osdk.Instance<Analysis>,
    _eventId: string,
  ) => {
    const route =
      analysis.type === "event deep dive"
        ? `/event-analysis/${analysis.id}`
        : analysis.type === "topic deep dive"
          ? `/topic-analysis/${analysis.id}`
          : "/";
    navigate(route);
  };

  const reportTitle = isLoading
    ? "Loading Report..."
    : report?.reportName || "Custom Report";
  const createdAt = report?.generatedAt
    ? new Date(report.generatedAt).toLocaleDateString()
    : "";
  useDocumentTitle(reportTitle, `Custom Report · ${createdAt}`);

  useEffect(() => {
    if (report) {
      const formattedDate = report.generatedAt
        ? format(new Date(report.generatedAt), "EEE, MMM dd, yyyy")
        : "Unknown Date";
      setPageTitle(
        report.reportName ?? "Custom Report",
        `Custom Report · ${formattedDate}`,
      );
    }
    return () => {
      setPageTitle("", "");
    };
  }, [report, setPageTitle]);

  const [isDownloading, setIsDownloading] = useState(false);

  const downloadDocx = async () => {
    if (!report?.reportAttachmentDocx) return;
    setIsDownloading(true);
    try {
      const [metadata, response] = await Promise.all([
        report.reportAttachmentDocx.fetchMetadata(),
        report.reportAttachmentDocx.fetchContents(),
      ]);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = metadata.filename || `${report.reportName || "report"}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download report:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      if (!contentRef.current) return;
      const cleanedHtml = cleanHtmlForCopy(contentRef.current);
      const clipboardItem = new ClipboardItem({
        "text/html": new Blob([cleanedHtml], { type: "text/html" }),
        "text/plain": new Blob([contentRef.current.textContent || ""], {
          type: "text/plain",
        }),
      });
      await navigator.clipboard.write([clipboardItem]);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy report:", err);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isAccessLoading || isLoading) {
    return (
      <div className={styles.centered}>
        <CircularProgress />
        <p>Loading report...</p>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className={styles.centered}>
        <Alert severity="error">
          You do not have access to Custom Reports.
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Go Back
        </Button>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.centered}>
        <Alert severity="error">Failed to load report</Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Go Back
        </Button>
      </div>
    );
  }

  if (!report) {
    return (
      <div className={styles.centered}>
        <Alert severity="warning">Report not found</Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Go Back
        </Button>
      </div>
    );
  }

  const formattedDate = report.generatedAt
    ? format(new Date(report.generatedAt), "EEE, MMM dd, yyyy")
    : "Unknown Date";

  return (
    <div className={styles.wrapper}>
      <section className={styles.container} ref={contentRef}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>{report.reportName}</h1>
            <p className={styles.subtitle}>
              Custom Report{report.type ? ` (${report.type})` : ""} ·{" "}
              {formattedDate}
            </p>
          </div>
          <div className={styles.actions}>
            {report.type === "dyad" && (
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => createDyadDialogRef.current?.show()}
                sx={{ textTransform: "none" }}
              >
                Add Dyad
              </Button>
            )}
            {report.type === "collection" && blockDocument && (
              <Button
                startIcon={<PictureAsPdfOutlinedIcon />}
                onClick={async () => {
                  setIsExportingPdf(true);
                  try {
                    await exportCollectionPdf({
                      title: report.reportName || "Geopolitical Overview",
                      subtitle: report.subtitle || "",
                      document: blockDocument,
                      clusters,
                    });
                  } finally {
                    setIsExportingPdf(false);
                  }
                }}
                disabled={isExportingPdf}
                sx={{ textTransform: "none" }}
              >
                {isExportingPdf ? "Exporting..." : "Export PDF"}
              </Button>
            )}
            {report.reportAttachmentDocx && (
              <Button
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={downloadDocx}
                disabled={isDownloading}
                sx={{ textTransform: "none" }}
              >
                {isDownloading ? "Downloading..." : "Download DOCX"}
              </Button>
            )}
            <Button
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={copyToClipboard}
              sx={{ textTransform: "none" }}
            >
              {copySuccess ? "Copied!" : "Copy Content"}
            </Button>
          </div>
        </div>
        <div className={styles.content}>
          {report.subtitle && report.type !== "dyad" && (
            <p className={styles.reportSubtitle}>{report.subtitle}</p>
          )}
          {blockDocument && report.type === "collection" && (
            <BlockDocumentMap document={blockDocument} />
          )}
          {tocBlock && blockDocument && (
            <TocBlockRenderer
              block={tocBlock}
              allBlocks={blockDocument.blocks}
            />
          )}
          {isBlockDocument && !blockDocument ? (
            <Alert severity="error">
              This report could not be rendered. The content format is invalid.
            </Alert>
          ) : blockDocumentWithoutToc ? (
            <BlockDocumentRenderer
              document={blockDocumentWithoutToc}
              reportType={report.type ?? undefined}
              eventSources={eventSources}
              isEventSourcesLoading={isEventSourcesLoading}
              missingEventIds={missingEventIds}
              userId={user?.id}
              sourceNumberMap={sourceNumberMap}
              onOpenAnalysis={handleOpenAnalysis}
              showShareDialog={(eventId) =>
                shareDialogRef.current?.show(eventId)
              }
            />
          ) : (
            <Markdown
              remarkPlugins={[foundryMarkdown]}
              components={{
                ...markdownComponents,
                a({ children, href }) {
                  const isInternalLink = href && href.startsWith("/");
                  if (isInternalLink) {
                    return (
                      <InternalLinkSourcePill
                        href={href}
                        eventSources={eventSources}
                        isLoading={isEventSourcesLoading}
                        missingEventIds={missingEventIds}
                        userId={user?.id}
                        sourceNumberMap={sourceNumberMap}
                        onOpenAnalysis={handleOpenAnalysis}
                        showShareDialog={(eventId) =>
                          shareDialogRef.current?.show(eventId)
                        }
                      />
                    );
                  }
                  return (
                    <a
                      href={href || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  );
                },
              }}
            >
              {report.content || "No content available."}
            </Markdown>
          )}
          {report.type === "collection" && (
            <div ref={keyEventsRef}>
              <KeyEventsChart />
            </div>
          )}
          <ShareDialog
            onShareSuccess={(postId) => toastRef.current?.shareSuccess(postId)}
            ref={shareDialogRef}
          />
          <SharedToLinkedInToast ref={toastRef} />
          <CreateDyadDialog ref={createDyadDialogRef} />
        </div>
      </section>
    </div>
  );
};

export default CustomReportPage;
